// src/pages/AnalyseResult.js
import React, { useState, useEffect } from 'react';
import styles from './AnalyseResult.module.css';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  Cpu,
  Smile,
  CheckSquare,
  Award,
  Info,
  Download,
  TrendingUp,
  Target,
  Zap,
  ArrowRight,
  Activity,
  Briefcase,
  Layers,
  Globe,
  Search,
  DollarSign,
  Link,
  BarChart2,
  Loader2,
  Sparkles,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';

// ── IMPORT DE REACT-PDF POUR LA GÉNÉRATION NATIVE ──
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';

// ─────────────────────────────────────────────────────────────
// FONCTIONS UTILITAIRES (Parsing sécurisé)
// ─────────────────────────────────────────────────────────────
const safeParse = (dataString) => {
  if (!dataString) return [];
  if (Array.isArray(dataString)) return dataString;
  if (typeof dataString === 'string') {
    try {
      const parsed = JSON.parse(dataString);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  return [];
};

const useParsedList = (dataString) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    setList(safeParse(dataString));
  }, [dataString]);
  return list;
};

// ─────────────────────────────────────────────────────────────
// STYLES DU PDF (Indépendant du CSS Web)
// ─────────────────────────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page: { padding: 30, backgroundColor: '#ffffff', fontFamily: 'Helvetica', color: '#334155' },
  header: { backgroundColor: '#0d4a68', padding: 20, borderRadius: 6, marginBottom: 20 },
  headerTitle: { color: '#ffffff', fontSize: 20, fontWeight: 'bold', marginBottom: 4 },
  headerSub: { color: '#e2e8f0', fontSize: 10 },
  row: { flexDirection: 'row', gap: 10, marginBottom: 15 },
  col: { flex: 1 },
  scoreBox: { backgroundColor: '#f8fafc', padding: 12, borderRadius: 4, flex: 1, alignItems: 'center', border: '1pt solid #e2e8f0' },
  scoreLabel: { fontSize: 8, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' },
  scoreValue: { fontSize: 18, fontWeight: 'bold', color: '#0ea5e9' },
  section: { marginBottom: 15 },
  sectionTitle: { fontSize: 12, fontWeight: 'bold', color: '#0f172a', borderBottom: '1pt solid #cbd5e1', paddingBottom: 4, marginBottom: 8 },
  card: { backgroundColor: '#f8fafc', padding: 8, borderRadius: 4, marginBottom: 8, border: '1pt solid #f1f5f9' },
  cardTitle: { fontSize: 10, fontWeight: 'bold', color: '#334155', marginBottom: 4 },
  tagCloud: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  tag: { backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3 6', borderRadius: 4, fontSize: 8 },
  tagWarning: { backgroundColor: '#fef3c7', color: '#b45309', padding: '3 6', borderRadius: 4, fontSize: 8 },
  tagError: { backgroundColor: '#fee2e2', color: '#b91c1c', padding: '3 6', borderRadius: 4, fontSize: 8 },
  bulletItem: { flexDirection: 'row', marginBottom: 4, alignItems: 'flex-start' },
  bulletPoint: { width: 12, fontSize: 10, color: '#94a3b8' },
  bulletText: { flex: 1, fontSize: 9, lineHeight: 1.4 },
  optiBox: { backgroundColor: '#f0fdf4', borderLeft: '2pt solid #22c55e', padding: 8, marginBottom: 8 },
  optiOriginal: { fontSize: 8, color: '#64748b', textDecoration: 'line-through', marginBottom: 4 },
  optiImproved: { fontSize: 9, color: '#166534', fontWeight: 'bold' },
});

// ─────────────────────────────────────────────────────────────
// COMPOSANT DOCUMENT PDF (Génération vectorielle pure)
// ─────────────────────────────────────────────────────────────
const AuditPDFDocument = ({ data }) => {
  const techCore = safeParse(data?.tech_core);
  const techCloud = safeParse(data?.tech_cloud);
  const stackMissing = safeParse(data?.stack_missing);
  const softSkills = safeParse(data?.soft_skills_detected);
  const blindSpots = safeParse(data?.resume_blind_spots);
  const quickWins = safeParse(data?.quick_wins);
  const optimizationBullets = safeParse(data?.optimization_bullets);

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        
        {/* EN-TÊTE */}
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.headerTitle}>{data?.detected_role || 'Poste non identifié'}</Text>
          <Text style={pdfStyles.headerSub}>
            {data?.seniority_level || 'Niveau inconnu'} • {data?.industry_relevance || 'Secteur non précisé'}
          </Text>
        </View>

        {/* SCORES PRINCIPAUX */}
        <View style={pdfStyles.row}>
          <View style={pdfStyles.scoreBox}>
            <Text style={pdfStyles.scoreLabel}>Score Global</Text>
            <Text style={pdfStyles.scoreValue}>{data?.global_score || 0}%</Text>
          </View>
          <View style={pdfStyles.scoreBox}>
            <Text style={pdfStyles.scoreLabel}>Valeur Marché</Text>
            <Text style={pdfStyles.scoreValue}>{data?.market_value_score || 0}%</Text>
          </View>
          <View style={pdfStyles.scoreBox}>
            <Text style={pdfStyles.scoreLabel}>Impact KPIs</Text>
            <Text style={pdfStyles.scoreValue}>{data?.content_impact_score || 0}%</Text>
          </View>
          <View style={pdfStyles.scoreBox}>
            <Text style={pdfStyles.scoreLabel}>Cohérence</Text>
            <Text style={pdfStyles.scoreValue}>{data?.semantic_coherence_score || 0}%</Text>
          </View>
        </View>

        {/* GRILLE À DEUX COLONNES */}
        <View style={pdfStyles.row}>
          
          {/* Colonne Gauche : Tech & Marché */}
          <View style={pdfStyles.col}>
            <View style={pdfStyles.section}>
              <Text style={pdfStyles.sectionTitle}>Analyse du Marché (France)</Text>
              <View style={pdfStyles.bulletItem}>
                <Text style={pdfStyles.bulletPoint}>•</Text>
                <Text style={pdfStyles.bulletText}>Offres actives : {data?.market_volume || 'N/A'}</Text>
              </View>
              <View style={pdfStyles.bulletItem}>
                <Text style={pdfStyles.bulletPoint}>•</Text>
                <Text style={pdfStyles.bulletText}>Salaire moyen : {data?.market_salary || 'N/A'}</Text>
              </View>
              <View style={pdfStyles.bulletItem}>
                <Text style={pdfStyles.bulletPoint}>•</Text>
                <Text style={pdfStyles.bulletText}>Standard ESCO : {data?.esco_role || 'N/A'}</Text>
              </View>
            </View>

            <View style={pdfStyles.section}>
              <Text style={pdfStyles.sectionTitle}>Tech Stack Audit</Text>
              {techCore.length > 0 && (
                <View style={pdfStyles.card}>
                  <Text style={pdfStyles.cardTitle}>Core Technologies</Text>
                  <View style={pdfStyles.tagCloud}>
                    {techCore.map((t, i) => <Text key={i} style={pdfStyles.tag}>{t}</Text>)}
                  </View>
                </View>
              )}
              {techCloud.length > 0 && (
                <View style={pdfStyles.card}>
                  <Text style={pdfStyles.cardTitle}>Cloud & Outils</Text>
                  <View style={pdfStyles.tagCloud}>
                    {techCloud.map((t, i) => <Text key={i} style={pdfStyles.tag}>{t}</Text>)}
                  </View>
                </View>
              )}
            </View>
            
            {stackMissing.length > 0 && (
              <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Standards Manquants</Text>
                <View style={pdfStyles.tagCloud}>
                  {stackMissing.map((m, i) => <Text key={i} style={pdfStyles.tagError}>{m}</Text>)}
                </View>
              </View>
            )}
          </View>

          {/* Colonne Droite : Soft Skills & Recos */}
          <View style={pdfStyles.col}>
            <View style={pdfStyles.section}>
              <Text style={pdfStyles.sectionTitle}>Soft Skills & Management</Text>
              <View style={pdfStyles.tagCloud}>
                {softSkills.map((s, i) => <Text key={i} style={pdfStyles.tag}>{s}</Text>)}
              </View>
              {data?.management_potential && (
                <View style={[pdfStyles.card, { marginTop: 10, backgroundColor: '#eff6ff' }]}>
                  <Text style={pdfStyles.cardTitle}>Potentiel Management</Text>
                  <Text style={pdfStyles.bulletText}>{data.management_potential}</Text>
                </View>
              )}
            </View>

            <View style={pdfStyles.section}>
              <Text style={pdfStyles.sectionTitle}>Zones d'Ombre (Gaps)</Text>
              {blindSpots.length === 0 ? (
                <Text style={pdfStyles.bulletText}>Aucune zone d'ombre majeure détectée.</Text>
              ) : (
                blindSpots.map((spot, i) => (
                  <View key={i} style={pdfStyles.bulletItem}>
                    <Text style={pdfStyles.bulletPoint}>!</Text>
                    <Text style={pdfStyles.bulletText}>{spot}</Text>
                  </View>
                ))
              )}
            </View>

            <View style={pdfStyles.section}>
              <Text style={pdfStyles.sectionTitle}>Quick Wins (Actions immédiates)</Text>
              {quickWins.map((win, i) => (
                <View key={i} style={pdfStyles.bulletItem}>
                  <Text style={[pdfStyles.bulletPoint, {color: '#22c55e'}]}>✓</Text>
                  <Text style={pdfStyles.bulletText}>{win}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* OPTIMISATIONS */}
        {optimizationBullets.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>Optimisation des expériences (Bullet Points)</Text>
            {optimizationBullets.map((opt, i) => (
              <View key={i} style={pdfStyles.optiBox}>
                <Text style={pdfStyles.optiOriginal}>Original : "{opt.original_idea || opt.original}"</Text>
                <Text style={pdfStyles.optiImproved}>Suggéré : "{opt.suggested_improvement || opt.improved}"</Text>
              </View>
            ))}
          </View>
        )}

      </Page>
    </Document>
  );
};


// ─────────────────────────────────────────────────────────────
// COMPOSANTS UI WEB (Inchagés)
// ─────────────────────────────────────────────────────────────
const ScoreGauge = ({ percentage, label, size = 'large' }) => {
  const isLarge = size === 'large';
  const radius = isLarge ? 50 : 35;
  const stroke = isLarge ? 10 : 7;
  const width = isLarge ? 120 : 90;
  const circumference = 2 * Math.PI * radius;
  const valid = isNaN(percentage) ? 0 : Math.min(Math.max(percentage, 0), 100);
  const offset = circumference - (valid / 100) * circumference;

  const color =
    valid >= 75
      ? 'var(--color-success)'
      : valid >= 50
      ? 'var(--color-warning)'
      : 'var(--color-error)';

  return (
    <div className={`${styles.scoreGauge} ${styles[size]}`}>
      <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
        <circle
          className={styles.gaugeBackground}
          strokeWidth={stroke}
          r={radius}
          cx={width / 2}
          cy={width / 2}
        />
        <circle
          className={styles.gaugeProgress}
          strokeWidth={stroke}
          stroke={color}
          r={radius}
          cx={width / 2}
          cy={width / 2}
          style={{ strokeDasharray: circumference, strokeDashoffset: offset }}
        />
      </svg>
      <div className={styles.scoreContent}>
        <span className={styles.scorePercentage} style={{ color }}>
          {valid}
        </span>
        {label && <span className={styles.scoreLabel}>{label}</span>}
      </div>
    </div>
  );
};

const MiniScoreItem = ({ percentage, title, subtitle }) => (
  <div className={styles.miniScoreItem}>
    <ScoreGauge percentage={percentage} size="small" />
    <div className={styles.miniScoreText}>
      <p className={styles.miniScoreTitle}>{title}</p>
      <p className={styles.miniScoreSub}>{subtitle}</p>
    </div>
  </div>
);

const MarketCard = ({ icon: Icon, title, value, subtext, accent }) => (
  <div className={styles.marketCard} style={{ '--accent': accent }}>
    <div className={styles.marketIconWrap}>
      <Icon size={20} strokeWidth={1.75} />
    </div>
    <div className={styles.marketCardBody}>
      <p className={styles.marketValue}>{value || 'N/A'}</p>
      <p className={styles.marketTitle}>{title}</p>
      {subtext && <p className={styles.marketSub}>{subtext}</p>}
    </div>
  </div>
);

const EscoSection = ({ role, uri, missing }) => {
  const missingList = useParsedList(missing);
  return (
    <div className={styles.escoBox}>
      <div className={styles.escoHeader}>
        <div className={styles.escoTitleGroup}>
          <div className={styles.escoIconWrap}>
            <Globe size={18} strokeWidth={1.75} />
          </div>
          <div>
            <p className={styles.escoLabel}>Standardisation ESCO (Europe)</p>
            <p className={styles.escoRole}>{role || 'Non identifié'}</p>
          </div>
        </div>
        {uri && (
          <a href={uri} target="_blank" rel="noreferrer" className={styles.escoLink}>
            <Link size={13} strokeWidth={2} /> Voir fiche officielle
          </a>
        )}
      </div>
      {missingList.length > 0 && (
        <div className={styles.escoMissing}>
          <p className={styles.escoMissingTitle}>
            <AlertTriangle size={13} strokeWidth={2} /> Compétences standards manquantes
          </p>
          <div className={styles.tagCloud}>
            {missingList.map((skill, i) => (
              <span key={i} className={styles.tagWarning}>{skill}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TagGroup = ({ title, items, variant = 'blue' }) => {
  const list = useParsedList(items);
  if (!list?.length) return null;
  return (
    <div className={styles.tagGroup}>
      <p className={styles.tagGroupTitle}>{title}</p>
      <div className={styles.tagCloud}>
        {list.map((item, i) => (
          <span key={i} className={`${styles.tag} ${styles[`tag--${variant}`]}`}>{item}</span>
        ))}
      </div>
    </div>
  );
};

const OptimizationCard = ({ items }) => {
  const list = useParsedList(items);
  if (!list?.length) return null;
  return (
    <div className={styles.optimizationSection}>
      <div className={styles.optimizationHeader}>
        <div className={styles.sectionIconWrap} style={{ '--icon-color': 'var(--color-accent)' }}>
          <Zap size={18} strokeWidth={1.75} />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>Optimisation des Bullet Points</h2>
          <p className={styles.sectionSub}>Réécriture à fort impact pour valoriser vos expériences</p>
        </div>
      </div>
      <div className={styles.optimizationGrid}>
        {list.map((item, i) => (
          <div key={i} className={styles.optimizationCard}>
            <div className={styles.optiOriginal}>
              <p className={styles.optiLabel}>
                <AlertTriangle size={13} strokeWidth={2} /> Original
              </p>
              <p className={styles.optiText}>« {item.original_idea || item.original} »</p>
            </div>
            <div className={styles.optiArrow}><ArrowRight size={18} strokeWidth={2} /></div>
            <div className={styles.optiImproved}>
              <p className={styles.optiLabel}>
                <CheckCircle size={13} strokeWidth={2} /> Suggéré (Impact +)
              </p>
              <p className={styles.optiText}>« {item.suggested_improvement || item.improved} »</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
  <div className={styles.sectionCard}>
    <div className={styles.sectionCardHeader}>
      <div className={styles.sectionIconWrap} style={{ '--icon-color': iconColor }}>
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <div>
        <h3 className={styles.sectionTitle}>{title}</h3>
        {subtitle && <p className={styles.sectionSub}>{subtitle}</p>}
      </div>
    </div>
    <div className={styles.sectionCardBody}>{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL WEB
// ─────────────────────────────────────────────────────────────
const AnalyseResult = ({ data }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const techCore          = useParsedList(data?.tech_core);
  const techCloud         = useParsedList(data?.tech_cloud);
  const stackObsolescence = useParsedList(data?.stack_obsolescence);
  const stackMissing      = useParsedList(data?.stack_missing);
  const softSkills        = useParsedList(data?.soft_skills_detected);
  const missingHardSkills = useParsedList(data?.missing_hard_skills);
  const blindSpots        = useParsedList(data?.resume_blind_spots);
  const quickWins         = useParsedList(data?.quick_wins);
  const upskilling        = useParsedList(data?.upskilling_path);

  const generatePDF = async () => {
    if (!data) return;
    setIsGeneratingPDF(true);
    
    try {
      // 1. Génération du Blob via @react-pdf/renderer
      const pdfBlob = await pdf(<AuditPDFDocument data={data} />).toBlob();

      // 2. Création et exécution du téléchargement
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      
      const safeRole = (data?.detected_role || 'Result').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `Audit-CV-${safeRole}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Erreur détaillée lors de la création du PDF vectoriel :", err);
      alert('Erreur lors de la génération du PDF. Vérifiez la console.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!data) return null;

  return (
    // On a retiré le "ref={resultRef}" car on ne capture plus le DOM
    <div className={styles.wrapper}>

      {/* ══════════ HEADER ══════════ */}
      <div className={styles.header}>
        <div className={styles.headerDecor} aria-hidden="true" />

        <div className={styles.headerMain}>
          <div className={styles.headerMeta}>
            <span className={styles.seniorityBadge}>
              {data.seniority_level || 'Niveau inconnu'}
            </span>
            {data.industry_relevance && (
              <span className={styles.industryBadge}>
                <Briefcase size={12} strokeWidth={2} />
                {data.industry_relevance}
              </span>
            )}
          </div>

          <h1 className={styles.headerTitle}>
            {data.detected_role || 'Poste déduit inconnu'}
          </h1>

          {data.file1_filename && (
            <p className={styles.headerFile}>
              <FileText size={13} strokeWidth={2} />
              {data.file1_filename}
            </p>
          )}
        </div>

        <button
          onClick={generatePDF}
          disabled={isGeneratingPDF}
          className={styles.downloadBtn}
          aria-label="Télécharger le rapport PDF"
        >
          {isGeneratingPDF
            ? <Loader2 size={15} strokeWidth={2} className={styles.spinIcon} />
            : <Download size={15} strokeWidth={2} />
          }
          {isGeneratingPDF ? 'Génération…' : 'Exporter PDF'}
        </button>
      </div>

      {/* ══════════ SCORES ══════════ */}
      <div className={styles.scoresSection}>
        <div className={styles.mainScore}>
          <ScoreGauge percentage={data.global_score} size="large" />
          <div className={styles.mainScoreText}>
            <p className={styles.mainScoreTitle}>Score Global</p>
            <p className={styles.mainScoreRationale}>{data.score_rationale}</p>
          </div>
        </div>
        <div className={styles.scoresDivider} />
        <div className={styles.miniScores}>
          <MiniScoreItem percentage={data.market_value_score} title="Attractivité" subtitle="Valeur marché" />
          <MiniScoreItem percentage={data.content_impact_score} title="Impact" subtitle="KPIs & ROI" />
          <MiniScoreItem percentage={data.semantic_coherence_score || 0} title="Cohérence" subtitle="CV vs Titre" />
        </div>
      </div>

      {/* ══════════ MARKET INSIGHTS ══════════ */}
      <div className={styles.marketSection}>
        <div className={styles.marketSectionHeader}>
          <div className={styles.sectionIconWrap} style={{ '--icon-color': 'var(--color-info)' }}>
            <BarChart2 size={16} strokeWidth={1.75} />
          </div>
          <p className={styles.marketSectionTitle}>Données marché</p>
        </div>

        <div className={styles.marketGrid}>
          <MarketCard icon={Search} title="Volume d'offres" value={data.market_volume} subtext="Offres actives (France)" accent="var(--color-info)" />
          <MarketCard icon={DollarSign} title="Salaire moyen" value={data.market_salary} subtext="Estimé sur offres récentes" accent="var(--color-success)" />
          <MarketCard icon={BarChart2} title="Mot-clé marché" value={data.market_term_used || data.market_search_term} subtext="Terme de recherche utilisé" accent="var(--color-primary-600)" />
        </div>

        <EscoSection role={data.esco_role} uri={data.esco_uri} missing={data.esco_missing || data.esco_missing_skills} />
      </div>

      {/* ══════════ GRILLE PRINCIPALE ══════════ */}
      <div className={styles.mainGrid}>
        <div className={styles.column}>
          <SectionCard icon={Cpu} iconColor="var(--color-primary-600)" title="Tech Stack Audit" subtitle="Analyse de votre stack technique">
            <TagGroup title="Core Technologies" items={JSON.stringify(techCore)} variant="blue" />
            <TagGroup title="Cloud & DevOps" items={JSON.stringify(techCloud)} variant="purple" />

            {(stackObsolescence.length > 0 || stackMissing.length > 0) && (
              <div className={styles.alertStack}>
                {stackObsolescence.length > 0 && (
                  <div className={styles.alertBox} data-variant="warning">
                    <p className={styles.alertTitle}><AlertTriangle size={14} strokeWidth={2} /> Risque d'obsolescence</p>
                    <ul className={styles.alertList}>
                      {stackObsolescence.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                )}
                {stackMissing.length > 0 && (
                  <div className={styles.alertBox} data-variant="danger">
                    <p className={styles.alertTitle}><Target size={14} strokeWidth={2} /> Standards manquants</p>
                    <ul className={styles.alertList}>
                      {stackMissing.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {data.stack_strengths && (
              <div className={styles.commentBox}>
                <Info size={15} strokeWidth={2} className={styles.commentIcon} />
                <p>{data.stack_strengths}</p>
              </div>
            )}
          </SectionCard>

          <SectionCard icon={Layers} iconColor="var(--color-error)" title="Gaps Critiques" subtitle="Points d'attention prioritaires">
            {missingHardSkills.length === 0 && blindSpots.length === 0 ? (
              <div className={styles.emptyState}>
                <ShieldCheck size={20} strokeWidth={1.75} />
                <p>Aucun gap critique détecté</p>
              </div>
            ) : (
              <ul className={styles.gapList}>
                {missingHardSkills.map((gap, i) => (
                  <li key={i} className={styles.gapItem} data-type="error">
                    <AlertTriangle size={14} strokeWidth={2} /><span>{gap}</span>
                  </li>
                ))}
                {blindSpots.map((spot, i) => (
                  <li key={i} className={styles.gapItem} data-type="warning">
                    <Activity size={14} strokeWidth={2} /><span>{spot}</span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <div className={styles.column}>
          <SectionCard icon={Smile} iconColor="var(--color-success)" title="Soft Skills & Leadership" subtitle="Compétences comportementales détectées">
            <div className={styles.softSkillsGrid}>
              {softSkills.map((skill, i) => (
                <span key={i} className={styles.softBadge}><CheckCircle size={13} strokeWidth={2} />{skill}</span>
              ))}
              {softSkills.length === 0 && <p className={styles.emptyText}>Aucun soft skill détecté</p>}
            </div>
            {data.management_potential && (
              <div className={styles.managementBadge}>
                <Sparkles size={14} strokeWidth={2} />
                <div>
                  <p className={styles.managementLabel}>Potentiel management</p>
                  <p className={styles.managementValue}>{data.management_potential}</p>
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard icon={TrendingUp} iconColor="var(--color-accent)" title="Quick Wins & Upskilling" subtitle="Plan d'action recommandé">
            {quickWins.length > 0 && (
              <div className={styles.recoBlock}>
                <p className={styles.recoBlockTitle}><Zap size={13} strokeWidth={2} /> Actions immédiates</p>
                <ul className={styles.recoList}>
                  {quickWins.map((win, i) => (
                    <li key={i} className={styles.recoItem} data-variant="success">
                      <CheckSquare size={14} strokeWidth={2} /><span>{win}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {upskilling.length > 0 && (
              <div className={styles.recoBlock}>
                <p className={styles.recoBlockTitle}><ChevronRight size={13} strokeWidth={2} /> Chemin de progression</p>
                <ul className={styles.recoList}>
                  {upskilling.map((up, i) => (
                    <li key={i} className={styles.recoItem} data-variant="purple">
                      <Award size={14} strokeWidth={2} /><span>{up}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {quickWins.length === 0 && upskilling.length === 0 && (
              <p className={styles.emptyText}>Aucune recommandation disponible</p>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ══════════ OPTIMISATION ══════════ */}
      {data?.optimization_bullets && <OptimizationCard items={data.optimization_bullets} />}
    </div>
  );
};

export default AnalyseResult;